import moment from 'moment';
import { calculerNbreAnnees, finPeriodeBudgetaire, moisDebutPdv } from '@/services/ligneCommande/calculDataLigne';
import { genererBudget } from '@/services/gestionPanneau/genererPanneau';
import { calculNetBudget } from '@/services/devisCommandes/calcul/calculRemisePanneau';
import calculArrondi from '@/services/calcul/calculArrondi';
import { calculHonoraire, updateModeCalculHonoraireBudget } from '@/services/devisCommandes/calcul/calculHonoraire';
import { ANNEE } from '@/configuration/devisConf';

function decouperPeriodes(dateDebut, dateFin, dateFinPremiereEcheance, moisDebut = null) {
  const periodes = [];
  let cursor = moment(dateDebut).startOf('day');
  const fin = moment(dateFin).startOf('day');

  if (!dateFinPremiereEcheance) dateFinPremiereEcheance = fin.clone();

  const finP1 = moment(dateFinPremiereEcheance).startOf('day');
  periodes.push({ dateDebut: cursor.toDate(), dateFin: finP1.toDate() });
  cursor = finP1.clone().add(1, 'day');

  while (cursor.isSameOrBefore(fin)) {
    const finPeriode = moisDebut
      ? finPeriodeBudgetaire(cursor, moisDebut)
      : cursor.clone().endOf('year').startOf('day');
    const finAnnee = moment.min(finPeriode, fin);
    periodes.push({ dateDebut: cursor.toDate(), dateFin: finAnnee.toDate() });
    cursor = finAnnee.clone().add(1, 'day');
  }

  return periodes;
}

export function genererBudgetsPanneau(budgetGeneration, pointDeVente = null) {
  const {
    dateDebut, dateFin, dateFinPremiereEcheance,
    prixBrutNegocie, remise, tlpe, fraisTechnique,
    modeCalculHonoraire,
    uniteQteBrut, uniteQteTlpe,
  } = budgetGeneration;

  const moisDebutExercice = moisDebutPdv(pointDeVente);
  const periodes = decouperPeriodes(dateDebut, dateFin, dateFinPremiereEcheance, moisDebutExercice);

  // Honoraires de référence : calculés sur les valeurs annuelles (non proratisées)
  const refHonoraires = calculHonoraire(modeCalculHonoraire, budgetGeneration.prixNet, tlpe, fraisTechnique);
  const refHonoraireFraisTraitement = modeCalculHonoraire ? modeCalculHonoraire.montantHonoraireFraisTraitement : 0;

  return periodes.map(periode => {
    const coeff = calculerNbreAnnees(periode.dateDebut, periode.dateFin);
    const budget = genererBudget();

    budget.dateDebut = periode.dateDebut;
    budget.dateFin = periode.dateFin;
    budget.prixBrutNegocie = calculArrondi(prixBrutNegocie * coeff, 5);
    budget.remise = { ...remise };
    budget.tlpe = tlpe != null ? calculArrondi(tlpe * coeff, 5) : 0;
    budget.fraisTechnique = fraisTechnique;
    budget.modeCalculHonoraire = modeCalculHonoraire;

    budget.honoraires = refHonoraires;
    budget.honoraireFraisTraitement = refHonoraireFraisTraitement;

    // Champs quantité/prix unitaire
    budget.prixUnitaireBrut = prixBrutNegocie;
    budget.quantiteBrut = calculArrondi(coeff, 10);
    budget.uniteQteBrut = uniteQteBrut || ANNEE;
    budget.prixUnitaireTlpe = tlpe != null ? tlpe : 0;
    budget.quantiteTlpe = calculArrondi(coeff, 10);
    budget.uniteQteTlpe = uniteQteTlpe || ANNEE;

    calculNetBudget(budget);
    updateModeCalculHonoraireBudget(budget, modeCalculHonoraire);

    return budget;
  });
}

export function updateDataBudgetGenerationByBudgets(budgets, budgetGeneration) {
  if (!budgets || !budgets.length) return;

  const sorted = [...budgets].sort((a, b) => new Date(a.dateDebut) - new Date(b.dateDebut));
  const premier = sorted[0];
  const dernier = sorted[sorted.length - 1];

  budgetGeneration.dateDebut = premier.dateDebut;
  budgetGeneration.dateFin = dernier.dateFin;
  const dateFinPremiere = sorted.length > 1 ? premier.dateFin : null;
  budgetGeneration.dateFinPremiereEcheance = dateFinPremiere
    && moment(dateFinPremiere).isAfter(moment(premier.dateDebut))
    && moment(dateFinPremiere).isBefore(moment(dernier.dateFin))
    ? dateFinPremiere
    : null;

  const coeff = calculerNbreAnnees(dernier.dateDebut, dernier.dateFin);
  if (coeff && coeff > 0) {
    budgetGeneration.prixBrutNegocie = calculArrondi(dernier.prixBrutNegocie / coeff, 5);
    budgetGeneration.tlpe = dernier.tlpe != null
      ? calculArrondi(dernier.tlpe / coeff, 5)
      : null;
  }

  budgetGeneration.remise = { ...dernier.remise };
  budgetGeneration.fraisTechnique = dernier.fraisTechnique;
  budgetGeneration.modeCalculHonoraire = dernier.modeCalculHonoraire || null;
  budgetGeneration.uniteQteBrut = dernier.uniteQteBrut || ANNEE;
  budgetGeneration.uniteQteTlpe = dernier.uniteQteTlpe || ANNEE;

  calculNetBudget(budgetGeneration);
  updateModeCalculHonoraireBudget(budgetGeneration, budgetGeneration.modeCalculHonoraire);
}
